import { getClient } from '@sentry/nextjs';

type Replay = NonNullable<ReturnType<typeof import('@sentry/nextjs').getReplay>>;
let replayLoad: Promise<Replay | undefined> | undefined;

export function getLoadedReplay() {
  // Sentry's getReplay export also imports the recorder bundle. The client
  // lookup keeps that bundle behind the dynamic import below.
  return getClient()?.getIntegrationByName<Replay>('Replay');
}

export function loadReplay(): Promise<Replay | undefined> {
  const client = getClient();
  if (typeof window === 'undefined' || !client || client.getOptions().enabled === false) {
    return Promise.resolve(undefined);
  }

  const existingReplay = getLoadedReplay();
  if (existingReplay) return Promise.resolve(existingReplay);

  replayLoad ??= import('./replay-integration')
    .then(({ addIntegration, replayIntegration }) => {
      if (!getLoadedReplay()) {
        addIntegration(
          replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            minReplayDuration: 1000,
            beforeAddRecordingEvent(event) {
              if (
                event.data.tag === 'breadcrumb' &&
                event.data.payload.category === 'replay.hydrate-error' &&
                typeof document !== 'undefined'
              ) {
                // Sentry creates synthetic issues from this Replay breadcrumb outside beforeSend.
                // These classes identify HTML changed by browser translation.
                const classes = document.documentElement?.classList;
                if (classes?.contains('translated-ltr') || classes?.contains('translated-rtl')) {
                  return null;
                }
              }
              return event;
            },
          }),
        );
      }
      return getLoadedReplay();
    })
    .catch(() => {
      // An optional recording must not block a login or checkout. Allow retry.
      replayLoad = undefined;
      return undefined;
    });

  return replayLoad;
}
